import type {
  MaskitoPlugin,
  MaskitoPostprocessor,
  MaskitoPreprocessor,
} from "@maskito/core";
import type { MaskitoNumberParams } from "@maskito/kit";

import {
  isAllZeros,
  isEmptyOrAllZeros,
  isNonZeroDigit,
} from "./numberMaskUtils";

/**
 * State for tracking trailing zeros across Maskito's multi-pass validation.
 *
 * This state coordinates between the preprocessor (which detects trailing zero
 * scenarios) and postprocessor (which restores stripped zeros) to preserve
 * user-typed trailing zeros until they leave the field.
 */
interface TrailingZerosState {
  /**
   * The target value to restore if Maskito strips trailing zeros.
   */
  expectedValue: string | null;
  /**
   * Whether to preserve trailing zeros until the input loses focus.
   */
  preserveUntilBlur: boolean;
}

export interface TrailingZerosHandlers {
  preprocessor: MaskitoPreprocessor;
  postprocessor: MaskitoPostprocessor;
  blurPlugin: MaskitoPlugin;
}

/**
 * Creates processors to preserve trailing zeros during decimal input.
 *
 * When `minimumFractionDigits` is 0, Maskito's multi-pass validation strips
 * trailing zeros (e.g., "123.0" → "123."). This factory returns coordinated
 * processors that detect and restore trailing zeros, using closure state to
 * support multiple NumberField instances.
 */
export function createTrailingZerosHandlers(
  params: Pick<
    MaskitoNumberParams,
    "minimumFractionDigits" | "maximumFractionDigits"
  >,
): TrailingZerosHandlers {
  const maximumFractionDigits = params.maximumFractionDigits ?? 0;

  const state: TrailingZerosState = {
    expectedValue: null,
    preserveUntilBlur: false,
  };

  /** Detects trailing zero scenarios during user input. */
  const preprocessor: MaskitoPreprocessor = (
    { elementState, data },
    actionType,
  ) => {
    const { value, selection } = elementState;
    const [selStart, selEnd] = selection;
    const decimalIndex = value.indexOf(".");

    // Typing "0" after decimal with only zeros or nothing after it
    if (actionType === "insert" && data === "0" && decimalIndex !== -1) {
      const afterDecimal = value.slice(decimalIndex + 1);

      if (isEmptyOrAllZeros(afterDecimal)) {
        const zerosNeeded = afterDecimal.length + 1;
        if (zerosNeeded <= maximumFractionDigits) {
          state.expectedValue =
            value.slice(0, decimalIndex + 1) + "0".repeat(zerosNeeded);
          state.preserveUntilBlur = true;
        }
      }
    }

    // Deletion that results in trailing zeros
    if (
      (actionType === "deleteBackward" || actionType === "deleteForward") &&
      decimalIndex !== -1
    ) {
      let simulatedValue: string;

      if (selStart !== selEnd) {
        simulatedValue = value.slice(0, selStart) + value.slice(selEnd);
      } else if (actionType === "deleteBackward" && selStart > 0) {
        simulatedValue = value.slice(0, selStart - 1) + value.slice(selStart);
      } else if (actionType === "deleteForward" && selStart < value.length) {
        simulatedValue = value.slice(0, selStart) + value.slice(selStart + 1);
      } else {
        return { elementState, data };
      }

      const simDecimalIndex = simulatedValue.indexOf(".");
      if (simDecimalIndex !== -1) {
        const simAfterDecimal = simulatedValue.slice(simDecimalIndex + 1);
        const currentAfterDecimal = value.slice(decimalIndex + 1);

        const deletionCreatesTrailingZeros = isAllZeros(simAfterDecimal);
        const deletionRemovesAllTrailingZeros =
          simAfterDecimal === "" && isAllZeros(currentAfterDecimal);

        if (deletionCreatesTrailingZeros) {
          // Deleting a non-zero digit leaves trailing zeros (e.g., "123.50" → "123.0")
          // Preserve them so user can continue editing
          state.expectedValue = simulatedValue;
          state.preserveUntilBlur = true;
        } else if (deletionRemovesAllTrailingZeros) {
          // Deleting the last trailing zero (e.g., "456.0" → "456.")
          // Clear preservation since user is intentionally removing zeros
          state.expectedValue = null;
          state.preserveUntilBlur = false;
        }
      }
    }

    // Clear state on non-zero digit input
    if (actionType === "insert" && isNonZeroDigit(data)) {
      state.expectedValue = null;
      state.preserveUntilBlur = false;
    }

    return { elementState, data };
  };

  /** Restores trailing zeros if Maskito stripped them. */
  const postprocessor: MaskitoPostprocessor = ({ value, selection }) => {
    if (state.expectedValue !== null && state.preserveUntilBlur) {
      const targetValue = state.expectedValue;

      if (
        targetValue.startsWith(value) ||
        value.startsWith(targetValue.replace(/0+$/, ""))
      ) {
        return {
          value: targetValue,
          selection: [targetValue.length, targetValue.length],
        };
      }
    }

    return { value, selection };
  };

  /** Clears preservation state on blur. */
  const blurPlugin: MaskitoPlugin = (element) => {
    const onBlur = () => {
      state.expectedValue = null;
      state.preserveUntilBlur = false;
    };

    element.addEventListener("blur", onBlur);

    return () => {
      element.removeEventListener("blur", onBlur);
    };
  };

  return {
    preprocessor,
    postprocessor,
    blurPlugin,
  };
}
